/**
 * Top Page Animations
 *
 * 参考: SF_CorporateSite/assets/JS/top.js
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { whenFontsReady } from '../util/fonts.js';
gsap.registerPlugin(ScrollTrigger, SplitText);

// =========================================================
// Preloader - LocalStorage + SessionStorage 判定
// =========================================================

const isFirstVisitEver = !localStorage.getItem('visited');
localStorage.setItem('visited', 'true');

const isNewTab = !sessionStorage.getItem('tabOpened');
sessionStorage.setItem('tabOpened', 'true');

const showPreloader = isFirstVisitEver || isNewTab;

function startLoader(duration = 2) {
  const counter = document.querySelector('.js-top-preloader-counter');
  if (!counter) return;
  const obj = { value: 0 };
  gsap.to(obj, {
    value: 100,
    duration,
    ease: 'power1.inOut',
    onUpdate: () => {
      counter.textContent = Math.floor(obj.value).toString().padStart(2, '0');
    },
  });
}

const preloaderEl = document.querySelector('.js-top-preloader');

if (preloaderEl) {
  if (showPreloader) {
    const tl = gsap.timeline();
    tl.to('.js-top-preloader-logo', {
      opacity: 1,
      duration: 0.5,
      ease: 'power3.out',
    }).to(
      preloaderEl,
      {
        opacity: 0,
        duration: 1,
        ease: 'power3.out',
        onComplete: () => {
          preloaderEl.style.display = 'none';
        },
      },
      1.8
    );
    startLoader(1.5);
  } else {
    preloaderEl.style.display = 'none';
  }
}

// =========================================================
// KV テキスト・動画アニメーション
// =========================================================

const kvDelay = showPreloader ? 1.2 : 0;

const kvCopyEl = document.querySelector('.js-top-kv-copy');

if (kvCopyEl) {
  const kvtext = new SplitText(kvCopyEl, {
    type: 'lines, chars',
    linesClass: 'line',
  });

  gsap.from(kvtext.chars, {
    y: '100%',
    opacity: 0,
    stagger: 0.04,
    ease: 'power4.out',
    duration: 1,
    delay: showPreloader ? kvDelay + 1 : 0.3,
  });

  gsap.from('.js-top-kv-scroll', {
    opacity: 0,
    ease: 'power4.out',
    duration: 1,
    delay: showPreloader ? kvDelay + 1.5 : 0.8,
  });
}

// =========================================================
// Scroll Down マーキーアニメーション
// =========================================================

gsap.to('.js-top-kv-scroll-item', {
  xPercent: -100,
  repeat: -1,
  ease: 'none',
  duration: 5,
});

// =========================================================
// KV 動画 一時停止・再生
// =========================================================

const kvVideo = document.querySelector('.js-top-kv-movie-video');
const kvMovieBtn = document.querySelector('.js-top-kv-movie-btn');
if (kvVideo) {
  kvVideo.src = window.matchMedia('(max-width: 1024px)').matches
    ? kvVideo.dataset.srcSp
    : kvVideo.dataset.srcPc;
  kvVideo.load();
}

if (kvVideo && kvMovieBtn) {
  let isPlaying = true;

  const topVideoLabels =
    typeof window.spiceTop !== 'undefined' && window.spiceTop
      ? window.spiceTop
      : { videoPause: '動画を一時停止', videoPlay: '動画を再生' };

  kvMovieBtn.addEventListener('click', () => {
    if (isPlaying) {
      kvVideo.pause();
      kvMovieBtn.classList.add('is-paused');
      kvMovieBtn.setAttribute('aria-label', topVideoLabels.videoPlay);
    } else {
      kvVideo.play();
      kvMovieBtn.classList.remove('is-paused');
      kvMovieBtn.setAttribute('aria-label', topVideoLabels.videoPause);
    }
    isPlaying = !isPlaying;
  });
}

// =========================================================
// KV〜Works 背景色切り替え（ScrollTrigger）
// =========================================================

ScrollTrigger.create({
  trigger: '.js-top-kv',
  start: 'top 80%',
  endTrigger: '.js-top-works',
  end: 'bottom 20%',
  toggleClass: {
    targets: 'body',
    className: 'in-view',
  },
});

// =========================================================
// charsanim - ラベル文字を1文字ずつ出現（ScrollTrigger）
// =========================================================

gsap.utils.toArray('.js-chars-anim').forEach(el => {
  const split = new SplitText(el, { type: 'chars', charsClass: 'chars' });
  gsap.from(split.chars, {
    y: '100%',
    stagger: 0.03,
    ease: 'power3.out',
    duration: 0.6,
    scrollTrigger: {
      trigger: el,
      start: 'top 85%',
    },
  });
});

window.addEventListener('load', () => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      // js-reveal-lines - テキスト行をスクロールで下から出現
      gsap.utils.toArray('.js-reveal-lines').forEach(el => {
        const split = new SplitText(el, {
          type: 'lines',
          mask: 'lines',
          wordDelimiter: '',
        });
        gsap.set(el, { opacity: 1 });
        gsap.from(split.lines, {
          y: '100%',
          stagger: 0.06,
          ease: 'power3.out',
          duration: 0.8,
          scrollTrigger: {
            trigger: el,
            start: 'top 85%',
          },
        });
      });

      const aboutTextEl = document.querySelector('.js-top-about-text');
      if (aboutTextEl) {
        if (window.matchMedia('(min-width: 1025px)').matches) {
          const split = new SplitText(aboutTextEl, {
            type: 'lines',
            mask: 'lines',
            wordDelimiter: '',
          });
          gsap.from(split.lines, {
            y: '100%',
            stagger: 0.06,
            ease: 'power3.out',
            duration: 0.8,
            scrollTrigger: { trigger: aboutTextEl, start: 'top 85%' },
          });
        } else {
          gsap.from(aboutTextEl, {
            y: 30,
            opacity: 0,
            ease: 'power3.out',
            duration: 0.8,
            scrollTrigger: { trigger: aboutTextEl, start: 'top 85%' },
          });
        }
      }

      ScrollTrigger.refresh();
    });
  });
});

// =========================================================
// js-fade-up - 要素を下からフェードイン（ScrollTrigger）
// =========================================================

gsap.utils.toArray('.js-fade-up').forEach(el => {
  gsap.from(el, {
    y: 40,
    opacity: 0,
    ease: 'power3.out',
    duration: 0.8,
    scrollTrigger: {
      trigger: el,
      start: 'top 85%',
    },
  });
});

// =========================================================
// KV 動画 → About スクロール縮小アニメーション
// =========================================================

const kvMovieEl = document.querySelector('.p-top-kv__movie');
const kvSection = document.querySelector('.p-top-kv');
const aboutEl = document.querySelector('.js-top-about');

if (kvMovieEl && aboutEl && kvSection) {
  gsap.set(kvMovieEl, {
    position: 'fixed',
    top: '50%',
    left: '50%',
    xPercent: -50,
    yPercent: -50,
    width: '100vw',
    height: '100vh',
    zIndex: 0,
  });

  const mm = gsap.matchMedia();

  mm.add('(max-width: 1024px)', () => {
    // SP: FV終了直後からモノクロ + 透明度（縮小なし）
    gsap.to(kvMovieEl, {
      opacity: 0.3,
      filter: 'grayscale(1)',
      ease: 'none',
      scrollTrigger: {
        trigger: kvSection,
        start: 'bottom bottom',
        end: 'bottom center',
        scrub: true,
      },
    });

    // About 最下部到達後：absolute に切り替えてスクロール追従
    ScrollTrigger.create({
      trigger: aboutEl,
      start: 'bottom bottom',
      onEnter: () => {
        const rect = kvMovieEl.getBoundingClientRect();
        const aboutRect = aboutEl.getBoundingClientRect();
        gsap.set(kvMovieEl, {
          position: 'absolute',
          top: rect.top - aboutRect.top,
          left: rect.left - aboutRect.left,
          xPercent: 0,
          yPercent: 0,
          zIndex: 0,
        });
      },
      onLeaveBack: () => {
        const rect = kvMovieEl.getBoundingClientRect();
        gsap.set(kvMovieEl, {
          position: 'fixed',
          top: '50%',
          left: '50%',
          xPercent: -50,
          yPercent: -50,
          width: rect.width,
          height: rect.height,
          zIndex: 0,
        });
      },
    });

    return () => {
      gsap.set(kvMovieEl, {
        position: 'fixed',
        top: '50%',
        left: '50%',
        xPercent: -50,
        yPercent: -50,
        width: '100vw',
        height: '100vh',
      });
    };
  });

  mm.add('(min-width: 1025px)', () => {
    const shrinkTrigger = {
      trigger: aboutEl,
      start: 'center bottom',
      end: 'center center',
      scrub: true,
    };

    // PC: 縮小 + モノクロ + 透明度
    gsap.to(kvMovieEl, {
      width: '80vw',
      height: '45vw',
      top: 'calc(50% - 40px)',
      opacity: 0.3,
      filter: 'grayscale(1)',
      ease: 'none',
      scrollTrigger: shrinkTrigger,
    });

    if (kvVideo) {
      gsap.to(kvVideo, {
        scale: 1.0625,
        ease: 'none',
        scrollTrigger: shrinkTrigger,
      });
    }

    const kvInnerEl = kvSection.querySelector('.p-top-kv__inner');
    if (kvInnerEl) {
      gsap.to(kvInnerEl, {
        opacity: 0,
        ease: 'none',
        scrollTrigger: shrinkTrigger,
      });
    }

    // About 中央到達後：absolute に切り替えてスクロール追従
    ScrollTrigger.create({
      trigger: aboutEl,
      start: 'center center',
      onEnter: () => {
        const rect = kvMovieEl.getBoundingClientRect();
        const aboutRect = aboutEl.getBoundingClientRect();
        gsap.set(kvMovieEl, {
          position: 'absolute',
          top: rect.top - aboutRect.top,
          left: rect.left - aboutRect.left,
          xPercent: 0,
          yPercent: 0,
          zIndex: 0,
        });
      },
      onLeaveBack: () => {
        const rect = kvMovieEl.getBoundingClientRect();
        gsap.set(kvMovieEl, {
          position: 'fixed',
          top: '50%',
          left: '50%',
          xPercent: -50,
          yPercent: -50,
          width: rect.width,
          height: rect.height,
          zIndex: 0,
        });
      },
    });

    return () => {
      gsap.set(kvMovieEl, {
        position: 'fixed',
        top: '50%',
        left: '50%',
        xPercent: -50,
        yPercent: -50,
        width: '100vw',
        height: '100vh',
      });
      const kvInnerEl = kvSection.querySelector('.p-top-kv__inner');
      if (kvInnerEl) gsap.set(kvInnerEl, { opacity: 1 });
    };
  });

  // About 退場時にフェードアウト
  gsap.to(kvMovieEl, {
    opacity: 0,
    ease: 'none',
    scrollTrigger: {
      trigger: aboutEl,
      start: 'bottom 60%',
      end: 'bottom top',
      scrub: 1,
    },
  });
}

// =========================================================
// Works アイテム スクロール出現アニメーション（1件ずつ）
// =========================================================

gsap.utils.toArray('.js-top-works-item').forEach(item => {
  gsap.from(item, {
    y: 40,
    opacity: 0,
    ease: 'power3.out',
    duration: 0.8,
    scrollTrigger: {
      trigger: item,
      start: 'top 85%',
    },
  });
});

// Works サムネイルストーカー（absolute 追従版）
// =========================================================

const worksSection = document.querySelector('.js-top-works');
const workItems = document.querySelectorAll('.js-top-works-item');
const followerImg = document.querySelector('.js-top-works-follower-wrap');
const followerImgTag = followerImg?.querySelector('.js-top-works-follower-img');

if (worksSection && followerImg && followerImgTag) {
  // follower をアイテム中心に配置するため xPercent/yPercent で -50% オフセット
  gsap.set(followerImg, { xPercent: -50, yPercent: -50 });

  let worksX = 0;
  let worksY = 0;
  let worksMouseX = 0;
  let worksMouseY = 0;
  let isInWorks = false;

  worksSection.addEventListener('mouseenter', () => {
    isInWorks = true;
  });

  worksSection.addEventListener('mouseleave', () => {
    isInWorks = false;
    followerImg.style.opacity = '0';
  });

  worksSection.addEventListener('mousemove', e => {
    const rect = worksSection.getBoundingClientRect();
    worksMouseX = e.clientX - rect.left;
    worksMouseY = e.clientY - rect.top;
  });

  // NOTE: ページ遷移なしの静的ページのためクリーンアップ不要
  gsap.ticker.add(() => {
    if (!isInWorks) return;
    worksX += (worksMouseX - worksX) * 0.12;
    worksY += (worksMouseY - worksY) * 0.12;
    gsap.set(followerImg, { x: worksX, y: worksY });
  });

  workItems.forEach(item => {
    // 各アイテム内の非表示サムネイル画像（display:none）を取得してfollowerにセット
    const img = item.querySelector('.js-top-works-item-img');

    item.addEventListener('mouseenter', e => {
      if (!img || !img.src) return;
      const rect = worksSection.getBoundingClientRect();
      worksMouseX = e.clientX - rect.left;
      worksMouseY = e.clientY - rect.top;
      worksX = worksMouseX;
      worksY = worksMouseY;
      followerImgTag.src = img.src;
      gsap.set(followerImg, { x: worksX, y: worksY });
      followerImg.style.opacity = '1';
    });

    item.addEventListener('mouseleave', () => {
      followerImg.style.opacity = '0';
    });
  });
}

// =========================================================
// Gallery 横スクロール（rAF）
// 参考: SF_CorporateSite top.js — wrapper 全体を translateX で左方向に移動
// =========================================================

(function () {
  const prefersReduced = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;
  const wrapper = document.querySelector('.p-top-gallery__wrapper');

  if (!wrapper || prefersReduced) return;

  const firstList = wrapper.querySelector('.p-top-gallery__list');
  let listWidth = 0;
  let x = 0;
  const SPEED = window.matchMedia('(max-width: 1024px)').matches ? 0.6 : 1.5;

  function updateWidth() {
    if (firstList) {
      listWidth = firstList.getBoundingClientRect().width;
    }
  }

  function loop() {
    x -= SPEED;
    if (listWidth > 0 && -x >= listWidth) x = 0;
    wrapper.style.transform = `translateX(${x}px)`;
    requestAnimationFrame(loop);
  }

  window.addEventListener('load', () => {
    updateWidth();
    loop();
  });

  window.addEventListener('resize', updateWidth);
})();

// =========================================================
// 背景色切り替え（KV〜Works: 暗色 / Gallery〜: 明色）
// 参考: SF_CorporateSite top.js と同じ構造
// .p-top 背景色を GSAP で直接アニメーション管理
// CSS transition は使わず、GSAP が競合なく制御する。
// .in-dark / .in-service クラスはボタン・タグ等のスタイル切り替えにのみ使用。
// =========================================================

const pTop = document.querySelector('.p-top');
const kvEl = document.querySelector('.p-top-kv');
const worksEl = document.querySelector('.p-top-works');

const rootStyle = getComputedStyle(document.documentElement);
const colorBg = rootStyle.getPropertyValue('--ui-color-bg').trim();
const colorDark = rootStyle.getPropertyValue('--ui-color-dark').trim();
const colorYellow = rootStyle.getPropertyValue('--color-yellow-500').trim();
const bgDuration = 0.5;
const bgEase = 'power1.inOut';

if (pTop && kvEl && worksEl) {
  gsap.set(pTop, { backgroundColor: colorDark });

  ScrollTrigger.create({
    trigger: kvEl,
    start: 'top 80%',
    endTrigger: worksEl,
    end: 'bottom 20%',
    toggleClass: { targets: pTop, className: 'in-dark' },
    onLeave: () =>
      gsap.to(pTop, {
        backgroundColor: colorBg,
        duration: bgDuration,
        ease: bgEase,
      }),
    onEnterBack: () =>
      gsap.to(pTop, {
        backgroundColor: colorDark,
        duration: bgDuration,
        ease: bgEase,
      }),
  });
}

// =========================================================
// Service 背景色フェード（スクロールで黄色へ）
// =========================================================

const serviceBgEl = document.querySelector('.p-top-service');
if (serviceBgEl && pTop) {
  const enterService = () => {
    pTop.classList.add('in-service');
    gsap.to(pTop, {
      backgroundColor: colorYellow,
      duration: bgDuration,
      ease: bgEase,
    });
  };
  const leaveService = () => {
    pTop.classList.remove('in-service');
    gsap.to(pTop, {
      backgroundColor: colorDark,
      duration: bgDuration,
      ease: bgEase,
    });
  };

  ScrollTrigger.create({
    trigger: serviceBgEl,
    start: 'top 30%',
    end: 'bottom center',
    onEnter: enterService,
    onLeave: leaveService,
    onEnterBack: enterService,
    onLeaveBack: leaveService,
  });
}

// =========================================================
// About + Recruit 相互ブラーホバー
// =========================================================

const susCard = document.querySelector('.js-top-split-card-sus');
const recCard = document.querySelector('.js-top-split-card-rec');

if (susCard && recCard) {
  susCard.addEventListener('mouseenter', () => {
    susCard.classList.add('is-hover');
    recCard.classList.add('is-blur');
  });
  susCard.addEventListener('mouseleave', () => {
    susCard.classList.remove('is-hover');
    recCard.classList.remove('is-blur');
  });

  recCard.addEventListener('mouseenter', () => {
    recCard.classList.add('is-hover');
    susCard.classList.add('is-blur');
  });
  recCard.addEventListener('mouseleave', () => {
    recCard.classList.remove('is-hover');
    susCard.classList.remove('is-blur');
  });
}

// =========================================================
// c-concept アニメーション（360° Intelligence Firm 循環図）
// =========================================================

function initConceptAnim() {
  const figure = document.querySelector('.js-concept-figure');
  if (!figure) return;
  const ellipses = figure.querySelectorAll('.js-concept-ellipse');
  const units = figure.querySelectorAll('.js-concept-unit');
  const badges = figure.querySelectorAll('.js-concept-badge-group');
  if (!units.length) return;

  const place = () => {
    units.forEach(unit => {
      gsap.set(unit.querySelector('.js-concept-badge-group'), {
        x: parseFloat(unit.dataset.bx),
        y: parseFloat(unit.dataset.by),
      });
      gsap.set(unit.querySelector('.js-concept-text-group'), {
        x: parseFloat(unit.dataset.tx),
        y: parseFloat(unit.dataset.ty),
      });
    });
  };

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    place();
    gsap.set(ellipses, {
      opacity: 1,
      strokeDasharray: 'none',
      strokeDashoffset: 0,
    });
    gsap.set(units, { opacity: 1 });
    gsap.set(badges, { opacity: 1 });
    return;
  }

  whenFontsReady(() => {
    place();
    gsap.set(ellipses, {
      opacity: 1,
      strokeDasharray: '1000 1000',
      strokeDashoffset: 1000,
    });
    gsap.set(units, { opacity: 0 });
    gsap.set(badges, { opacity: 0 });

    const tl = gsap.timeline({
      scrollTrigger: { trigger: figure, start: 'top 80%', once: true },
    });
    ellipses.forEach((ellipse, i) => {
      tl.to(
        ellipse,
        { strokeDashoffset: 0, duration: 1.0, ease: 'power2.inOut' },
        i === 0 ? 0 : '-=0.72'
      );
    });
    tl.to(
      units,
      { opacity: 1, duration: 0.55, ease: 'power3.out', stagger: 0.1 },
      '-=0.3'
    );
    tl.to(badges, { opacity: 1, duration: 0.4 }, '<');
  });
}

initConceptAnim();
